import React from 'react';

import { FontAwesomeIcon } from '@schoolinks/ui-components';

import { Trans } from 'react-i18next';

import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';

import { faXmarkCircle } from '@fortawesome/pro-solid-svg-icons/faXmarkCircle';

import {
  NEXT_30_DAYS,
  PAST_30_DAYS,
} from 'components/Common/DateRangeFilter/constants';

import { toTitleCase } from 'utils/toTitleCase';

import GoogleMeetLogo from 'modules/CounselorMeetingScheduler/assets/google-meet-logo.png';
import ZoomLogo from 'modules/CounselorMeetingScheduler/assets/zoom-logo.png';
import AppleCalendarImage from 'modules/EventSchedulerV2/assets/apple-cal.png';
import GoogleCalendarImage from 'modules/EventSchedulerV2/assets/google-cal.png';
import MicrosoftCalendarImage from 'modules/EventSchedulerV2/assets/microsoft-outlook.png';
import MicrosoftTeams from 'modules/EventSchedulerV2/K12Admin/assets/microsoft-teams-logo.png';

export const EVENT_SCHEDULE_LINK = '/events/event-schedule';
export const EVENT_TEMPLATES_LINK = '/events/event-templates';
export const BLOCKED_DATES_LINK = '/events/blocked-dates';
export const PAST_EVENTS_LINK = '/events/past-events';
export const PENDING_REQUESTS_LINK = '/events/pending-requests';
export const PARTNER_EVENTS_LINK = '/events/partner-events';

export const NAVIGATION_MAP = Object.freeze({
  1: EVENT_SCHEDULE_LINK,
  2: EVENT_TEMPLATES_LINK,
  3: BLOCKED_DATES_LINK,
  4: PAST_EVENTS_LINK,
  5: PENDING_REQUESTS_LINK,
  6: PARTNER_EVENTS_LINK,
});

export const NEW_EVENT_BUTTON_OPTIONS = t => [
  {
    title: t('EventSchedulerV2.Constants.Event'),
    description: t('EventSchedulerV2.Constants.CreateEventDescription'),
    url: '/events/event-schedule/new',
  },
  {
    title: t('EventSchedulerV2.Constants.EventTemplate'),
    description: t('EventSchedulerV2.Constants.CreateEventTemplateDescription'),
    url: '/events/event-templates/new',
  },
];

export const INTERNAL_EVENT_MANAGEMENT = 'internal_event_management';
export const COLLEGE_PARTNER_MANAGEMENT = 'college_partner_management';

export const REQUEST_CONFIRMED = 'approved';
export const REQUEST_DECLINED = 'rejected';
export const REQUEST_PENDING = 'requested';
export const REQUEST_REMOVED = 'removed';
export const NOT_REGISTERED = 'not_registered';
export const SUGGESTED_EVENTS = 'suggested_events';

export const EVENT_TYPE_STATUS_MAP = Object.freeze({
  [REQUEST_CONFIRMED]: { label: 'REQUEST CONFIRMED', color: 'green' },
  [REQUEST_DECLINED]: { label: 'REQUEST DECLINED', color: 'red' },
  [REQUEST_PENDING]: { label: 'REQUEST PENDING', color: 'orange' },
});

export const EVENT_STATUS_BUTTON_MAP = Object.freeze({
  [REQUEST_CONFIRMED]: {
    label: 'Change to declined',
    color: 'red',
    hasIconRight: true,
    variant: 'outlined',
    icon: 'arrow',
  },
  [REQUEST_DECLINED]: {
    label: 'Change to confirmed',
    color: 'blue',
    hasIconRight: true,
    variant: 'outlined',
    icon: 'arrow',
  },
  [REQUEST_PENDING]: {
    label: 'Confirm request',
    color: 'gray',
    hasIconLeft: true,
    variant: 'outlined',
    icon: <FontAwesomeIcon color="greenDark" icon={faCircleCheck} />,
  },
});

export const CONFIRM_KEY = 'confirm';
export const DECLINE_KEY = 'decline';

export const REP_VISIT_FORMAT_KEY = 'rep_visit';
export const OTHER_FORMAT_KEY = 'other';
export const WORKSHOP_FORMAT_KEY = 'workshop';
export const VOLUNTEER_FORMAT_KEY = 'volunteer';
export const FIELD_TRIP_FORMAT_KEY = 'field_trip';
export const COLLEGE_REP_VISIT_FORMAT_KEY = 'college_rep_visit';
export const PANEL_FORMAT_KEY = 'panel';
export const FAIR_FORMAT_KEY = 'fair';

export const EVENT_TYPE_CHIP_MAP = t => ({
  [OTHER_FORMAT_KEY]: {
    color: 'gray',
    label: t('EventSchedulerV2.Constants.Other'),
  },
  [REP_VISIT_FORMAT_KEY]: {
    color: 'violet',
    label: t('EventSchedulerV2.Constants.PartnerVisit'),
  },
  repVisit: {
    color: 'violet',
    label: t('EventSchedulerV2.Constants.PartnerVisit'),
  },
  fair: {
    color: 'indigo',
    label: t('EventSchedulerV2.Constants.Fair'),
  },
  panel: {
    color: 'yellow',
    label: t('EventSchedulerV2.Constants.Panel'),
  },
  [WORKSHOP_FORMAT_KEY]: {
    color: 'teal',
    label: t('EventSchedulerV2.Constants.Workshop'),
  },
  [VOLUNTEER_FORMAT_KEY]: {
    color: 'pink',
    label: t('EventSchedulerV2.Constants.Volunteer'),
  },
  [FIELD_TRIP_FORMAT_KEY]: {
    color: 'green',
    label: t('EventSchedulerV2.Constants.FieldTrip'),
  },
  fieldTrip: {
    color: 'green',
    label: t('EventSchedulerV2.Constants.FieldTrip'),
  },
  [COLLEGE_REP_VISIT_FORMAT_KEY]: {
    color: 'orange',
    label: t('EventSchedulerV2.Constants.CollegeRepVisit'),
  },
  collegeRepVisit: {
    color: 'orange',
    label: t('EventSchedulerV2.Constants.CollegeRepVisit'),
  },
});

export const HIDDEN_EVENT_TYPES = ['other'];

export const ACCESS_KEY_LABEL_MAP = t =>
  Object.freeze({
    students: t('EventSchedulerV2.Constants.Students'),
    guardians: t('EventSchedulerV2.Constants.Guardians'),
    college_reps: t('EventSchedulerV2.Constants.CollegePartners'),
    industry_partners: t('EventSchedulerV2.Constants.IndustryPartners'),
    k12_admins: t('EventSchedulerV2.Constants.Staff'),
  });

export const EVENT_FORMAT_LIST = t => [
  {
    name: t('EventSchedulerV2.Constants.Fair'),
    description: t('EventSchedulerV2.Constants.FairDescription'),
    value: 'fair',
  },
  {
    name: t('EventSchedulerV2.Constants.Panel'),
    description: t('EventSchedulerV2.Constants.PanelDescription'),
    value: 'panel',
  },
  {
    name: t('EventSchedulerV2.Constants.PartnerVisit'),
    description: t('EventSchedulerV2.Constants.PartnerVisitDescription'),
    value: 'rep_visit',
  },
  {
    name: t('EventSchedulerV2.Constants.CollegeRepVisit'),
    description: t('EventSchedulerV2.Constants.CollegeRepVisitDescription'),
    value: COLLEGE_REP_VISIT_FORMAT_KEY,
  },
  {
    name: t('EventSchedulerV2.Constants.Workshop'),
    description: t('EventSchedulerV2.Constants.WorkshopDescription'),
    value: WORKSHOP_FORMAT_KEY,
  },
  {
    name: t('EventSchedulerV2.Constants.Volunteer'),
    description: t('EventSchedulerV2.Constants.VolunteerDescription'),
    value: VOLUNTEER_FORMAT_KEY,
  },
  {
    name: t('EventSchedulerV2.Constants.FieldTrip'),
    description: t('EventSchedulerV2.Constants.FieldTripDescription'),
    value: FIELD_TRIP_FORMAT_KEY,
  },
  {
    name: t('EventSchedulerV2.Constants.Other'),
    description: t('EventSchedulerV2.Constants.OtherDescription'),
    value: 'other',
  },
];

export const STUDENT_KEY = 'students';
export const GUARDIAN_KEY = 'guardians';
export const COLLEGE_REPS_KEY = 'college_reps';
export const INDUSTRY_PARTNER_KEY = 'industry_partners';
export const STAFF_KEY = 'k12_admins';
export const DISTRICT_INDUSTRY_PARTNER = 'district_industry_partner';
export const DISTRICT_COLLEGE_REP = 'district_college_rep';
export const DISTRICT_GUARDIAN = 'district_guardian';
export const ALL_PARTICIPANTS_KEY = 'all_participants';

export const EVENT_PARTICIPANT_LIST = t => [
  { label: t('EventSchedulerV2.Constants.Students'), value: STUDENT_KEY },
  { label: t('EventSchedulerV2.Constants.Guardians'), value: GUARDIAN_KEY },
  {
    label: toTitleCase(t('EventSchedulerV2.Constants.CollegePartners')),
    value: COLLEGE_REPS_KEY,
  },
  {
    label: toTitleCase(t('EventSchedulerV2.Constants.IndustryPartners')),
    value: INDUSTRY_PARTNER_KEY,
  },
  { label: t('EventSchedulerV2.Constants.Staff'), value: STAFF_KEY },
];

export const EXTERNAL_LINK_POPOVER_DATA = t =>
  Object.freeze({
    description: t(
      'EventSchedulerV2.Constants.WantToAddEventToDifferentCalendar',
    ),
    logos: [
      {
        name: 'Zoom Meeting',
        logo: ZoomLogo,
        alt: 'zoom-meeting',
        href: 'https://www.notion.so/Zoom-Personal-Meeting-Link-Instructions-cdfdec26fdf94f36a6f6f09559305ae8',
      },
      {
        name: 'Microsoft Teams',
        logo: MicrosoftTeams,
        alt: 'microsoft-teams',
        href: 'https://www.notion.so/Microsoft-Teams-Personal-Meeting-Link-Instructions-e96b09cf0dc945e8940e44174e7f430b',
      },
      {
        name: 'Google Meet',
        logo: GoogleMeetLogo,
        alt: 'google-meet',
        href: 'https://www.notion.so/Google-Meet-Personal-Meeting-Link-Instructions-39f60c8c55c24cd4b2ebe4fe56254ce5',
      },
    ],
  });

export const DAY_MAP = Object.freeze({
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
});

export const CONFIRMED_STATUS = 'confirmed';
export const DECLINED_STATUS = 'declined';
export const PENDING_STATUS = 'pending';
export const REGISTERED_STATUS = 'registered';
export const LOCKED_STATUS = 'locked';

export const IN_PERSON = 'in_person';
export const VIRTUAL = 'virtual';

export const ATTENDANCE_FORMAT_EMOJI_MAP = Object.freeze({
  in_person: ' 🚗',
  virtual: ' 🖥',
});

export const ATTENDANCE_FORMAT_LABEL_MAP = t =>
  Object.freeze({
    [IN_PERSON]: t('EventSchedulerV2.Constants.InPerson'),
    [VIRTUAL]: t('EventSchedulerV2.Constants.Virtual'),
  });

export const ALL_REPORTS_KEY = 'all';
export const COLLEGE_REPS_REPORTS_KEY = 'college_reps';
export const INDUSTRY_PARTNER_REPORTS_KEY = 'industry_partners';
export const STUDENT_REGISTRATIONS_REPORTS_KEY = 'student_registrations';
export const STAFF_REGISTRATIONS_REPORTS_KEY = 'staff_registrations';

export const TIME_ZONES = [
  {
    name: 'EST/IET',
    timeZone: 'America/New_York',
  },
  {
    name: 'CST',
    timeZone: 'America/Chicago',
  },
  {
    name: 'MST/PNT',
    timeZone: 'America/Denver',
  },
  {
    name: 'PST',
    timeZone: 'America/Los_Angeles',
  },
];

export const TEMPLATE_KEY = 'template';
export const EVENT_KEY = 'event';
export const PAST_EVENT_KEY = 'past_event';

export const PENDING_KEY = 'pending';

export const EVENT_CARD_COUNT_BADGES = t => ({
  [STUDENT_KEY]: t('EventSchedulerV2.Constants.Students'),
  [GUARDIAN_KEY]: t('EventSchedulerV2.Constants.Guardians'),
  [COLLEGE_REPS_KEY]: toTitleCase(
    t('EventSchedulerV2.Constants.CollegePartners'),
  ),
  [INDUSTRY_PARTNER_KEY]: toTitleCase(
    t('EventSchedulerV2.Constants.IndustryPartners'),
  ),
  [PENDING_KEY]: t('EventSchedulerV2.Constants.PendingRequests'),
  [STAFF_KEY]: t('EventSchedulerV2.Constants.Staff'),
});

export const PAST_EVENT_CARD_COUNT_BADGES = ({ studentAttendeesCount, t }) => ({
  [STUDENT_KEY]: t('EventSchedulerV2.Constants.StudentsPastEvent', {
    value: studentAttendeesCount ? 'attended' : 'registered',
  }),
  [GUARDIAN_KEY]: t('EventSchedulerV2.Constants.GuardiansRegistered'),
  [COLLEGE_REPS_KEY]: t('EventSchedulerV2.Constants.CollegeRepsConfirmed'),
  [INDUSTRY_PARTNER_KEY]: t(
    'EventSchedulerV2.Constants.IndustryPartnersConfirmed',
  ),
  [STAFF_KEY]: t('EventSchedulerV2.Constants.StaffRegistered'),
});

export const PAST_EMPLOYER_EVENT_CARD_COUNT_BADGES = t => ({
  [STUDENT_KEY]: t('EventSchedulerV2.Constants.Students'),
  [GUARDIAN_KEY]: t('EventSchedulerV2.Constants.Guardians'),
  [STAFF_KEY]: t('EventSchedulerV2.Constants.Staff'),
});

export const COUNT_BADGE_COLORS = ({ studentAttendeesCount }) => ({
  [COLLEGE_REPS_KEY]: 'green',
  [INDUSTRY_PARTNER_KEY]: 'green',
  k12_admins: 'green',
  [PENDING_KEY]: 'orange',
  [GUARDIAN_KEY]: 'gray',
  [STUDENT_KEY]: studentAttendeesCount ? 'green' : 'gray',
});

export const EVENT_FORMAT = 'EVENT_FORMAT';
export const EVENT_NAME = 'EVENT_NAME';
export const EVENT_DESCRIPTION = 'EVENT_DESCRIPTION';
export const EVENT_CAREER_CLUSTERS = 'EVENT_CAREER_CLUSTERS';
export const EVENT_ALLOW_MULTIPLE_PARTICIPANT =
  'EVENT_ALLOW_MULTIPLE_PARTICIPANT';
export const EVENT_CONTACTS = 'EVENT_CONTACTS';
export const EVENT_DATE_TIME = 'EVENT_DATE_TIME';
export const EVENT_LOCATION = 'EVENT_LOCATION';
export const EVENT_MEETING_LINK = 'EVENT_MEETING_LINK';
export const EVENT_PARTICIPANT_COUNT = 'EVENT_PARTICIPANT_COUNT';
export const EVENT_PARTICIPANT_DURATION = 'EVENT_PARTICIPANT_DURATION';
export const EVENT_PARTICIPANT_TYPE = 'EVENT_PARTICIPANT_TYPE';
export const EVENT_REQUIRE_MEETING_LINK = 'EVENT_REQUIRE_MEETING_LINK';
export const EVENT_SCOPES = 'EVENT_SCOPES';
export const NUMBER_OF_ORGS_TO_SHOW = 3;
export const NUMBER_OF_ORGS_TO_SHOW_POPOVER = 10;

export const K12ADMIN_API_PATH =
  'v1/event-scheduler/k12-admin/events/calendar-events/';
export const MEMBER_API_PATH =
  'v1/event-scheduler/member/events/calendar-events/';
export const K12_ADMIN_PARTNER_EVENT_API_PATH =
  'v1/event-scheduler/k12-admin/partner-events/calendar-events/';

export const MAX_LETTER_COUNT = 80;

export const SINGULAR_TIME_KEY = 'singular';
export const RANGE_TIME_KEY = 'range';
export const MEMBER_KEY = 'member';
export const K12_ADMIN_KEY = 'k12_admin';
export const ADDED_MANUALLY_LABEL = t =>
  t('EventSchedulerV2.Constants.AddedManually');

export const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const COLLEGE_REP_OPTION = t => ({
  value: COLLEGE_REPS_KEY,
  type: DISTRICT_COLLEGE_REP,
  label: t('EventSchedulerV2.Constants.CollegePartners'),
});

export const INDUSTRY_PARTNER_OPTION = t => ({
  value: INDUSTRY_PARTNER_KEY,
  type: DISTRICT_INDUSTRY_PARTNER,
  label: t('EventSchedulerV2.Constants.IndustryPartners'),
});

export const INVITE_EXTERNAL_ORG_OPTIONS = t => [
  COLLEGE_REP_OPTION(t),
  INDUSTRY_PARTNER_OPTION(t),
];

export const ACCESS_TYPE_EDIT_KEY = 'edit';
export const ACCESS_TYPE_FULL_KEY = 'full';
export const ACCESS_TYPE_VIEW_KEY = 'view';

export const SURVEY_TYPE = 'survey';
export const ORGANIZATION_REGISTRATION_SURVEY_TYPE =
  'organization_registration_form';
export const STUDENT_REGISTRATION_SURVEY_TYPE = 'student_registration_form';
export const INSTRUCTION_TYPE = 'instructions';
export const EMAIL_TYPE = 'email';

export const DEADLINE_TO_UNREGISTER_FOR_MEMBER = 3; // member can't unregister if there is less than 3 days are left for the event

export const THANK_YOU_EMAIL_KEY = 'event_scheduler_thank_you_email';
export const CONFIRMATION_EMAIL_KEY = 'event_scheduler_accepting_email';

export const STUDENT_REGISTRATION_FORM = `student_registration_form`;
export const STUDENT_FOLLOW_UP_SURVEY = `student_follow_up`;
export const ORGANIZATION_REGISTRATION_FORM = `organization_registration_form`;
export const ORGANIZATION_FOLLOW_UP_SURVEY = `organization_follow_up`;

export const K12_ADMIN_SUPPORT_LINK =
  'https://support.schoolinks.com/en/articles/4513084-staff-how-to-subscribe-to-schoolinks-events-in-your-personal-calendar';
export const MEMBER_SUPPORT_LINK =
  'https://support.schoolinks.com/en/articles/10459583-industry-partner-overview-of-events';
export const STUDENT_SUPPORT_LINK =
  'https://support.schoolinks.com/en/articles/6616891-student-how-to-subscribe-to-schoolinks-events-in-your-personal-calendar';

export const GET_INSTRUCTION_FILES_QUERY = 'instruction_files_query';
export const GET_EVENT_EMAILS_QUERY = 'event_emails_query';
export const GET_EVENT_SURVEYS_QUERY = 'event_surveys_query';
export const SCHOOL_TYPE = 'schools';
export const GRADE_TYPE = 'grades';
export const TAG_TYPE = 'tags';
export const STUDENT_USER = 'student';
export const GUARDIAN_USER = 'guardian';
export const MEMBER_USER = 'member';
export const K12_ADMIN_USER = 'k12Admin';

export const MEMBER_SIDE_BAR_FILTERS = t => [
  {
    label: t('EventSchedulerV2.Constants.Registered'),
    value: REQUEST_CONFIRMED,
    badgeProps: {
      color: 'green',
      variant: 'filled',
    },
  },
  {
    label: t('EventSchedulerV2.Constants.PendingTitle'),
    value: REQUEST_PENDING,
    badgeProps: {
      color: 'orange',
      variant: 'filled',
    },
  },
  {
    label: t('EventSchedulerV2.Constants.DeclinedTitle'),
    value: REQUEST_DECLINED,
    badgeProps: {
      color: 'red',
      variant: 'filled',
    },
  },
  {
    label: t('EventSchedulerV2.Constants.SuggestedEvents'),
    value: SUGGESTED_EVENTS,
    badgeProps: {
      color: 'yellow',
      variant: 'filled',
    },
  },
];

export const NUMBER_OF_EVENTS_TO_SHOW = 3;

export const MEMBER_EVENTS_SEARCH_THRESHOLD = 2;
export const SCHOOL_KEY = 'School';
export const DISTRICT_KEY = 'District';

export const HE_SCHOOL_KEY = 'he_school';
export const EMPLOYER_KEY = 'employer';

export const EMAIL_ERROR_MESSAGE = t =>
  t('EventSchedulerV2.Constants.EmailAlreadyInUse');
export const MEMBER_EMAIL_ERROR_MESSAGE = t =>
  t('EventSchedulerV2.Constants.EmailAssociatedWithMemberAccount');

export const MEMBER_DASHBOARD_TAB_KEY = 1;
export const EVENT_OPENINGS_TAB_KEY = 2;
export const HOST_EVENTS_TAB_KEY = 3;

export const STATES = [
  { label: 'Alabama', id: 'Alabama' },
  { label: 'Alaska', id: 'Alaska' },
  { label: 'Arizona', id: 'Arizona' },
  { label: 'Arkansas', id: 'Arkansas' },
  { label: 'California', id: 'California' },
  { label: 'Colorado', id: 'Colorado' },
  { label: 'Connecticut', id: 'Connecticut' },
  { label: 'Delaware', id: 'Delaware' },
  { label: 'Florida', id: 'Florida' },
  { label: 'Georgia', id: 'Georgia' },
  { label: 'Hawaii', id: 'Hawaii' },
  { label: 'Idaho', id: 'Idaho' },
  { label: 'Illinois', id: 'Illinois' },
  { label: 'Indiana', id: 'Indiana' },
  { label: 'Iowa', id: 'Iowa' },
  { label: 'Kansas', id: 'Kansas' },
  { label: 'Kentucky', id: 'Kentucky' },
  { label: 'Louisiana', id: 'Louisiana' },
  { label: 'Maine', id: 'Maine' },
  { label: 'Maryland', id: 'Maryland' },
  { label: 'Massachusetts', id: 'Massachusetts' },
  { label: 'Michigan', id: 'Michigan' },
  { label: 'Minnesota', id: 'Minnesota' },
  { label: 'Mississippi', id: 'Mississippi' },
  { label: 'Missouri', id: 'Missouri' },
  { label: 'Montana', id: 'Montana' },
  { label: 'Nebraska', id: 'Nebraska' },
  { label: 'Nevada', id: 'Nevada' },
  { label: 'New Hampshire', id: 'New Hampshire' },
  { label: 'New Jersey', id: 'New Jersey' },
  { label: 'New Mexico', id: 'New Mexico' },
  { label: 'New York', id: 'New York' },
  { label: 'North Carolina', id: 'North Carolina' },
  { label: 'North Dakota', id: 'North Dakota' },
  { label: 'Ohio', id: 'Ohio' },
  { label: 'Oklahoma', id: 'Oklahoma' },
  { label: 'Oregon', id: 'Oregon' },
  { label: 'Pennsylvania', id: 'Pennsylvania' },
  { label: 'Rhode Island', id: 'Rhode Island' },
  { label: 'South Carolina', id: 'South Carolina' },
  { label: 'South Dakota', id: 'South Dakota' },
  { label: 'Tennessee', id: 'Tennessee' },
  { label: 'Texas', id: 'Texas' },
  { label: 'Utah', id: 'Utah' },
  { label: 'Vermont', id: 'Vermont' },
  { label: 'Virginia', id: 'Virginia' },
  { label: 'Washington', id: 'Washington' },
  { label: 'West Virginia', id: 'West Virginia' },
  { label: 'Wisconsin', id: 'Wisconsin' },
  { label: 'Wyoming', id: 'Wyoming' },
];

export const EVENT_FORMAT_FILTER_OPTIONS = [
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.Fair" />,
    id: 'fair',
    color: 'indigo',
  },
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.Panel" />,
    id: 'panel',
    color: 'yellow',
  },
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.PartnerVisit" />,
    id: 'rep_visit',
    color: 'violet',
  },
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.CollegeRepVisit" />,
    id: COLLEGE_REP_VISIT_FORMAT_KEY,
    color: 'orange',
  },
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.Workshop" />,
    id: 'workshop',
    color: 'teal',
  },
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.Volunteer" />,
    id: 'volunteer',
    color: 'pink',
  },
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.FieldTrip" />,
    id: FIELD_TRIP_FORMAT_KEY,
    color: 'green',
  },
  { label: <Trans i18nKey="EventSchedulerV2.Constants.Other" />, id: 'other' },
];

export const EVENT_LOCATION_FILTER_OPTIONS = [
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.InPerson" />,
    id: 'in_person',
  },
  {
    label: <Trans i18nKey="EventSchedulerV2.Constants.Virtual" />,
    id: 'virtual',
  },
];

export const AFFILIATION_KEY = 'affiliation';
export const CRM_AFFILIATION_KEY = 'crm_affiliation';
export const AFFILIATION_FILTER_KEY = 'confirmed_affiliations';
export const CRM_AFFILIATION_FILTER_KEY = 'confirmed_crm_affiliations';

export const EVENTS_KEY = 'events';
export const PARTNER_EVENTS_KEY = 'partner_events';
export const PAST_EVENTS_KEY = 'past_events';

export const PARTNER_EVENT_CSV_LIST = t => [
  {
    value: ALL_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.AllEventsContacts'),
  },
  {
    value: STUDENT_REGISTRATIONS_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.StudentRegistrations'),
  },
];

export const PAST_EVENT_CSV_LIST = t => [
  {
    value: ALL_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.AllEventsContacts'),
  },
  {
    value: STUDENT_REGISTRATIONS_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.StudentRegistrationsAttendance'),
  },
  {
    value: STAFF_REGISTRATIONS_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.StaffRegistrationsAttendance'),
  },
];

export const EVENT_CSV_LIST = t => [
  {
    value: ALL_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.AllEventsContacts'),
  },
  {
    value: INDUSTRY_PARTNER_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.IndustryPartnerEventsContacts'),
  },
  {
    value: COLLEGE_REPS_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.CollegePartnerEventsContacts'),
  },
  {
    value: STUDENT_REGISTRATIONS_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.StudentRegistrations'),
  },
  {
    value: STAFF_REGISTRATIONS_REPORTS_KEY,
    label: t('EventSchedulerV2.Constants.StaffRegistrations'),
  },
];

export const EXPORT_CSV_MAP = {
  [EVENTS_KEY]: t => EVENT_CSV_LIST(t),
  [PARTNER_EVENTS_KEY]: t => PARTNER_EVENT_CSV_LIST(t),
  [PAST_EVENTS_KEY]: t => PAST_EVENT_CSV_LIST(t),
};

export const MEMBER_EVENT_MGMT_CAPABILITY = 'organization_event_management';

export const MEMBER_EVENT_DISTRICT_PAGE_DATE_GROUP_FORMAT = 'YYYYMMDD';

export const ATTENDEE_TYPE_MAP = {
  [K12_ADMIN_KEY]: 'Staff',
  [GUARDIAN_USER]: 'Guardian',
  [STUDENT_USER]: 'Student',
};

export const ALL_DISTRICTS_RADIO_KEY = '0';
export const DISTRICTS_WITHIN_RADIUS_RADIO_KEY = '1';
export const SELECTED_DISTRICTS_RADIO_KEY = '2';

export const ELIGIBLE_DISTRICT_RADIO_OPTIONS = t => [
  {
    value: ALL_DISTRICTS_RADIO_KEY,
    label: t('EventSchedulerV2.Constants.AllDistricts'),
  },
  {
    value: DISTRICTS_WITHIN_RADIUS_RADIO_KEY,
    label: t('EventSchedulerV2.Constants.DistrictsSetRadius'),
  },
  {
    value: SELECTED_DISTRICTS_RADIO_KEY,
    label: t('EventSchedulerV2.Constants.SelectedDistricts'),
  },
];

export const MILE_RADIUS_OPTIONS = [10, 25, 50, 75, 100, 150, 200];

export const ALL_SCHOOL_ACCESS = 'all';
export const SPECIFIC_SCHOOL_ACCESS = 'specific';

export const CALENDAR_LIST = [
  { name: 'Apple Calendar', image: AppleCalendarImage },
  { name: 'Microsoft Outlook', image: MicrosoftCalendarImage },
  { name: 'Google Calendar', image: GoogleCalendarImage },
];

export const EVENT_LOCATION_TYPE = 'location';
export const EVENT_VIRTUAL_URL_TYPE = 'virtual-url';

export const EVENT_LOCATION_FIELD_CHIPS = t => [
  {
    value: EVENT_LOCATION_TYPE,
    label: t('EventSchedulerV2.Constants.InPerson'),
  },
  {
    value: EVENT_VIRTUAL_URL_TYPE,
    label: t('EventSchedulerV2.Constants.Virtual'),
  },
];

export const LIST_DEFAULT_ORDER_BY = 'start_datetime,created';

export const DEFAULT_DATE_RANGE = NEXT_30_DAYS;
export const DEFAULT_PAST_DATE_RANGE = PAST_30_DAYS;
export const DISTRICT_INITIAL_EVENTS_COUNT = 3;

export const EVENT_APPLICATION_STATUS_MAP = {
  [REQUEST_CONFIRMED]: t => ({
    color: 'greenDark',
    icon: faCircleCheckSolid,
    label: t('EventSchedulerV2.Constants.Approved'),
  }),
  [REQUEST_PENDING]: t => ({
    color: 'orangeDark',
    icon: faQuestionCircle,
    label: t('EventSchedulerV2.Constants.Pending'),
  }),
  [REQUEST_DECLINED]: t => ({
    color: 'redDark',
    icon: faXmarkCircle,
    label: t('EventSchedulerV2.Constants.Declined'),
  }),
};

export const MAXIMUM_PARTICIPANT_COUNT_REACHED_ERROR =
  'Student max participants reached.';
export const REGISTRATION_TIME_EXPIRED_ERROR =
  'Registration time expired for this event.';

export const EVENT_LIST_VIEW_TYPE = Object.freeze({
  Calendar: 'calendar',
  List: 'list',
});

export const CALENDAR_COLOR_MAP = {
  [FAIR_FORMAT_KEY]: 'indigoXtraDark',
  [COLLEGE_REP_VISIT_FORMAT_KEY]: 'orangeXtraDark',
  [REP_VISIT_FORMAT_KEY]: 'violetXtraDark',
  [PANEL_FORMAT_KEY]: 'yellowXtraDark',
  [WORKSHOP_FORMAT_KEY]: 'tealXtraDark',
  [VOLUNTEER_FORMAT_KEY]: 'pinkXtraDark',
  [FIELD_TRIP_FORMAT_KEY]: 'greenXtraDark',
  [OTHER_FORMAT_KEY]: 'blueXtraDark',
};

export const K12_ADMIN_REGISTRATION_DISABLED_STATUSES = {
  OPEN: 'open',
  IS_PAST: 'is_past',
  CUTOFF_PASSED: 'cutoff_passed',
  MAX_PARTICIPANTS_REACHED: 'max_participants_reached',
};

export const DISTRICT_EVENT_STATS_SORT_OPTIONS = t => [
  {
    label: t(
      'EventSchedulerV2.TerritoryDistrictEventsDrawerHeader.MostEventsThisSchoolYear',
    ),
    value: '-this_year_registered_event_count',
  },
  {
    label: t(
      'EventSchedulerV2.TerritoryDistrictEventsDrawerHeader.LeastEventsThisSchoolYear',
    ),
    value: 'this_year_registered_event_count',
  },
  {
    label: t(
      'EventSchedulerV2.TerritoryDistrictEventsDrawerHeader.MostEventsAllTime',
    ),
    value: '-registered_event_count',
  },
  {
    label: t(
      'EventSchedulerV2.TerritoryDistrictEventsDrawerHeader.LeastEventsAllTime',
    ),
    value: 'registered_event_count',
  },
  {
    label: t(
      'EventSchedulerV2.TerritoryDistrictEventsDrawerHeader.MostDaysElapsedSinceLastVisit',
    ),
    value: '-days_since_last_registered_event',
  },
  {
    label: t(
      'EventSchedulerV2.TerritoryDistrictEventsDrawerHeader.LeastDaysElapsedSinceLastVisit',
    ),
    value: 'days_since_last_registered_event',
  },
];

export const DISTRICT_WITH_EVENTS_DEFAULT_SORT =
  '-this_year_registered_event_count';

export const DISTRICT_WITHOUT_EVENTS_DEFAULT_SORT =
  'this_year_registered_event_count';

export const UNIQUE_AREA_TERRITORY = 'unique_area_territory';
export const NATIONWIDE_COVERAGE_TERRITORY = 'nationwide_coverage_territory';

export const TERRITORY_SETTING_TYPES = t => [
  {
    label: t('EventSchedulerV2.SetTerritoryModal.UniqueArea'),
    value: UNIQUE_AREA_TERRITORY,
  },
  {
    label: t('EventSchedulerV2.SetTerritoryModal.NationwideCoverage'),
    value: NATIONWIDE_COVERAGE_TERRITORY,
  },
];

export const ADD_DISTRICTS_BY_STATE = 'ADD_DISTRICTS_BY_STATE';
export const ADD_SCHOOLS_BY_STATE = 'ADD_SCHOOLS_BY_STATE';
export const ADD_BY_RADIUS = 'ADD_BY_RADIUS';

export const UNIQUE_AREA_TERRITORY_SETTINGS = t => [
  {
    name: t('EventSchedulerV2.Constants.AddDistrictsByState'),
    description: t('EventSchedulerV2.Constants.AddDistrictsByStateDescription'),
    value: ADD_DISTRICTS_BY_STATE,
  },
  {
    name: t('EventSchedulerV2.Constants.AddHighSchoolsByState'),
    description: t(
      'EventSchedulerV2.Constants.AddHighSchoolsByStateDescription',
    ),
    value: ADD_SCHOOLS_BY_STATE,
  },
  {
    name: t('EventSchedulerV2.Constants.AddByRadius'),
    description: t('EventSchedulerV2.Constants.AddByRadiusDescription'),
    value: ADD_BY_RADIUS,
  },
];

export const TERRITORY_TYPE_MAP = {
  USER: 'user', // territory set by user
  SYSTEM: 'system', // historical territory data
  NONE: 'none', // territory not configured
};

export const MY_TERRITORY_FILTER = 'my_territory';
export const ALL_FILTER = 'all';

export const TERRITORY_FILTER_EVENT_OPENINGS = t => [
  {
    label: t('EventSchedulerV2.Constants.MyTerritory'),
    value: MY_TERRITORY_FILTER,
  },
  { label: t('EventSchedulerV2.Constants.All'), value: ALL_FILTER },
];

export const SUGGESTED_EVENTS_COUNT = 50;
